import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { usePlacesAutocomplete } from "@/hooks/use-places-autocomplete";
import type { HelpPortalId } from "@/lib/help-portal";
import { parseGoogleAddressComponents } from "@/lib/parse-google-address-components";
import { useState } from "react";
import { z } from "zod";
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter } from "../ui/card";
import { FormFieldWrapper } from "../ui/form-field-wrapper";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { SuggestionList } from "../ui/suggestion-list";
import { Switch } from "../ui/switch";
import { useAppForm } from "../ui/tanstack-form";

export const PartyFormSchema = z.object({
  name: z.string(),
  company_name: z.string(),
  email: z.string(),
  phone: z.string(),
  address1: z.string(),
  address2: z.string(),
  city: z.string(),
  state: z.string(),
  zipcode: z.string(),
  is_active: z.boolean(),
  is_exempt: z.boolean(),
});

export type PartyFormValues = z.infer<typeof PartyFormSchema>;

interface PartyFormProps {
  partyData: PartyFormValues | null;
  onSubmit: (data: PartyFormValues) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** When true, fields are disabled and Save is hidden (government read-only). */
  readOnly?: boolean;
  /** Help Center the Exempt switch opens. */
  helpPortal?: HelpPortalId;
  /** Rendered inside the card above the save button (e.g. questions using this party) */
  children?: React.ReactNode;
}

export function PartyForm({
  partyData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  readOnly = false,
  helpPortal = "operator",
  children,
}: PartyFormProps) {
  const [placesAutoCompleteInput, setPlacesAutoCompleteInput] = useState("");
  const { suggestions } = usePlacesAutocomplete(placesAutoCompleteInput, {
    includedRegionCodes: ["us"],
    includedPrimaryTypes: ["street_address"],
  });

  const form = useAppForm({
    defaultValues: {
      name: partyData?.name ?? "",
      company_name: partyData?.company_name ?? "",
      email: partyData?.email ?? "",
      phone: partyData?.phone ?? "",
      address1: partyData?.address1 ?? "",
      address2: partyData?.address2 ?? "",
      city: partyData?.city ?? "",
      state: partyData?.state ?? "",
      zipcode: partyData?.zipcode ?? "",
      is_active: partyData?.is_active ?? true,
      is_exempt: partyData?.is_exempt ?? false,
    },
    validators: {
      onSubmit: PartyFormSchema,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
  };

  const handleAddressSuggestionClick = async (
    suggestion: google.maps.places.AutocompleteSuggestion,
  ) => {
    const place = suggestion.placePrediction?.toPlace();
    if (!place) {
      return;
    }
    await place.fetchFields({
      fields: ["addressComponents"],
    });

    if (!place?.addressComponents) {
      return;
    }

    const { address1, address2, city, state, zipcode } =
      parseGoogleAddressComponents(place.addressComponents);

    form.setFieldValue("address1", address1);
    form.validateField("address1", "change");
    form.setFieldValue("address2", address2);
    form.validateField("address2", "change");
    form.setFieldValue("city", city);
    form.validateField("city", "change");
    form.setFieldValue("state", state);
    form.validateField("state", "change");
    form.setFieldValue("zipcode", zipcode);
    form.validateField("zipcode", "change");
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <form.AppForm>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <fieldset
              disabled={readOnly}
              className="flex flex-col gap-4 border-0 p-0 m-0 min-w-0 w-full"
            >
              <form.AppField
                name="name"
                validators={{ onChange: PartyFormSchema.shape.name }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Name">
                    <Input
                      id="party-name"
                      placeholder="Name"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
              <form.AppField
                name="company_name"
                validators={{
                  onChange: PartyFormSchema.shape.company_name,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Company name">
                    <Input
                      id="party-company-name"
                      placeholder="Company name (optional)"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
              <form.AppField
                name="email"
                validators={{ onChange: PartyFormSchema.shape.email }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Email">
                    <Input
                      id="party-email"
                      type="email"
                      placeholder="Email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
              <form.AppField
                name="phone"
                validators={{ onChange: PartyFormSchema.shape.phone }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Phone">
                    <Input
                      id="party-phone"
                      placeholder="Phone"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
              <form.AppField
                name="address1"
                validators={{ onChange: PartyFormSchema.shape.address1 }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Address 1">
                    <SuggestionList
                      closeOnBlur
                      suggestions={suggestions}
                      renderSuggestion={(suggestion) => (
                        <span>{suggestion.placePrediction?.text?.text}</span>
                      )}
                      getSuggestionKey={(suggestion) =>
                        suggestion.placePrediction?.placeId ?? ""
                      }
                      onSuggestionSelect={handleAddressSuggestionClick}
                    >
                      <Input
                        id="party-address1"
                        placeholder="Enter street address (start typing to search)"
                        value={field.state.value}
                        onChange={(e) => {
                          field.handleChange(e.target.value);
                          setPlacesAutoCompleteInput(e.target.value);
                        }}
                        onBlur={field.handleBlur}
                      />
                    </SuggestionList>
                  </FormFieldWrapper>
                )}
              </form.AppField>
              <form.AppField
                name="address2"
                validators={{ onChange: PartyFormSchema.shape.address2 }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Address 2">
                    <Input
                      id="party-address2"
                      placeholder="Address 2"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
              <div className="grid grid-cols-2 gap-4">
                <form.AppField
                  name="city"
                  validators={{ onChange: PartyFormSchema.shape.city }}
                >
                  {(field) => (
                    <FormFieldWrapper field={field} label="City">
                      <Input
                        id="party-city"
                        placeholder="City"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </FormFieldWrapper>
                  )}
                </form.AppField>
                <form.AppField
                  name="state"
                  validators={{ onChange: PartyFormSchema.shape.state }}
                >
                  {(field) => (
                    <FormFieldWrapper field={field} label="State">
                      <Input
                        id="party-state"
                        placeholder="State"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </FormFieldWrapper>
                  )}
                </form.AppField>
              </div>
              <form.AppField
                name="zipcode"
                validators={{ onChange: PartyFormSchema.shape.zipcode }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Zipcode">
                    <Input
                      id="party-zipcode"
                      placeholder="Zipcode"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
            </fieldset>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
              <fieldset
                disabled={readOnly}
                className="flex flex-wrap items-center gap-x-6 border-0 p-0 m-0"
              >
                <form.AppField
                  name="is_active"
                  validators={{ onChange: PartyFormSchema.shape.is_active }}
                >
                  {(field) => (
                    <div className="flex items-center gap-2">
                      <Switch
                        id="party-active"
                        checked={field.state.value}
                        onCheckedChange={field.handleChange}
                      />
                      <Label htmlFor="party-active">Active</Label>
                    </div>
                  )}
                </form.AppField>
                <form.AppField
                  name="is_exempt"
                  validators={{ onChange: PartyFormSchema.shape.is_exempt }}
                >
                  {(field) => (
                    <div className="flex items-center gap-2">
                      <Switch
                        id="party-exempt"
                        checked={field.state.value}
                        onCheckedChange={field.handleChange}
                      />
                      <Label htmlFor="party-exempt">Exempt</Label>
                    </div>
                  )}
                </form.AppField>
              </fieldset>
              <ContextHelpButton
                articleId="party-exempt"
                helpPortal={helpPortal}
                iconOnly
                variant="ghost"
                className="h-7 w-7"
              />
              <span className="text-muted-foreground basis-full text-sm">
                Applies only when this party is the OMT. Fees due are zero.
                Registration and renewal are still required.
              </span>
            </div>
            {children ? (
              <div className="min-w-0 border-t pt-6">{children}</div>
            ) : null}
          </form>
        </form.AppForm>
      </CardContent>
      <CardFooter className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {readOnly ? "Close" : "Cancel"}
        </Button>
        {readOnly ? null : (
          <Button
            type="button"
            onClick={() => form.handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving…" : "Save"}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
